<script setup lang="ts">
import type { IProps } from './Duties.types'

const { members } = defineProps<IProps>()
const getOnlyMembersWithDuties = computed(() => members.filter(member => member.duties && member.supervisor))
</script>

<template>
  <div class="corporateGovernanceDuties">
    <h3 class="corporateGovernanceDuties__title">
      {{  $t('investor.corporateGovernance.main.duties.title') }}
    </h3>

    <div class="corporateGovernanceDuties__grid">
      <div
        v-for="(member) in getOnlyMembersWithDuties"
        :key="member.boardMemberName"
        class="corporateGovernanceDuties__box"
      >
        <div class="corporateGovernanceDuties__top">
          <h2
            class="corporateGovernanceDuties__group"
          >
            {{ $t('investor.corporateGovernance.main.duties.group') }}
          </h2>
    
          <p
            class="corporateGovernanceDuties__name"
          >
            {{ member.boardMemberName }}
          </p>
    
          <p
            class="corporateGovernanceDuties__role"
          >
            {{ member.boardMemberRole }}
          </p>
        </div>
  
        <div class="corporateGovernanceDuties__bottom">
          <p
            class="corporateGovernanceDuties__duty"
          >
            {{ $t('investor.corporateGovernance.main.duties.dutyLabel') }}
          </p>
  
          <ul
            v-if="member.duties"
            class="corporateGovernanceDuties__list"
          >
            <li
              v-for="(duty, index) in member.duties"
              :key="index"
              class="corporateGovernanceDuties__item"
            >
              {{ duty }}
            </li>
          </ul>
  
          <p
            class="corporateGovernanceDuties__duty"
          >
            {{ $t('investor.corporateGovernance.main.duties.supervisor') }}
          </p>
  
          <ul
            v-if="member.supervisor?.length"
            class="corporateGovernanceDuties__list"
          >
            <li
              v-for="(item, index) in member.supervisor"
              :key="index"
              class="corporateGovernanceDuties__item"
            >
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="corporateGovernanceDuties__box corporateGovernanceDuties__box--whiteBackground">
      <h3 class="corporateGovernanceDuties__boxTitle">
        {{ $t('investor.corporateGovernance.main.duties.it.title') }}
      </h3>

      <p class="corporateGovernanceDuties__boxLabel">
        {{ $t('investor.corporateGovernance.main.duties.it.label') }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.corporateGovernanceDuties {
  padding: 4.8rem 2.4rem;
  background-color: $gray-100;

  &__box {
    display: flex;
    flex-direction: column;
    margin-bottom: 2.4rem;
    border: 1px solid $neutral-50;
    border-radius: 1.2rem;

    &--whiteBackground {
      background-color: $white;
    }
  }

  &__title {
    @include h9;

    padding-bottom: 4.8rem;
    color: $text-400;
    text-align: center;
  }

  &__group {
    @include body-2;

    padding-bottom: 1.2rem;
    color: $text-400;
  }

  &__name {
    @include h9;

    padding-bottom: .4rem;
    color: $text-400;
  }

  &__role {
    @include body-1-medium;

    color: $text-300;
  }

  &__top {
    padding: 3.2rem;
    border-bottom: 1px solid $neutral-50;
  }

  &__bottom {
    height: 100%;
    padding: 3.2rem;
    background-color: $white;
    border-radius: 1.2rem;
  }

  &__duty {
    @include h10;

    padding-bottom: .4rem;
    color: $text-400;
  }

  &__list {
    margin-bottom: 2.2rem;
    padding-left: 2.2rem;
    list-style-type: disc;
  }

  &__item {
    @include body-2;

    color: $text-400;
  }

  &__boxTitle {
    @include h10;

    padding: 6.4rem 3.2rem .8rem 3.2rem;
    color: $text-400;
    text-align: center;
  }

  &__boxLabel {
    @include body-2;

    padding: 0 3.2rem 3.2em 3.2rem;
    color: $text-400;
    text-align: center;
  }

  @include atXS {
    padding: 9.4rem 6.4rem;

    &__title {
      @include h7;
    }
  }

  @include atM {
    &__grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2.4rem;
      max-width: 130rem;
      margin: auto;
    }

    &__box {
      margin: 0;
    }

    &__title {
      max-width: 86rem;
      margin: auto;
    }

    &__box--whiteBackground {
      width: 69rem;
      margin: 2.4rem auto 0;
    }
  }
}
</style>
